import { IFInitWalletRsp } from "@/api/apiRsp/IFWalletRsp";
import { GameOriginalBetBack } from "@/api/event/IFCustomStore";
import { Qb } from "@/assets/svg-icon";
import CurrencyComponent from "@/components/currencyComponent";
import ButtonUI from "@/componentsUI/Button";
import LoadingUI from "@/componentsUI/LoadingUI/LoadingUI";
import SpinUI from "@/componentsUI/Spin";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import {
  useGlobalCurrentWallet,
  useGlobalWalletStore,
  useUpdateGlobalOriginalBalance,
} from "@/store/commonStore";
import { useWalletAll, useWalletSwitch } from "@/store/walletStore";
import EventMgr from "@/utils/EventMgr";
import useDialogManager from "@/utils/useDialogManager";
import { ChevronDownIcon, LockIcon } from "@/componentsUI/Icon";
import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import styles from "./DropdownWallet.module.less";

const GUIDE_CHANGE_BIND_CURRENCY = "GUIDE_CHANGE_BIND_CUURENCY";
const DISABLED_SWITCH_CURRENCIES = ["FUN"];

const isSameWallet = (
  currentWallet?: IFInitWalletRsp,
  nextWallet?: IFInitWalletRsp
) => {
  if (!currentWallet || !nextWallet) {
    return currentWallet === nextWallet;
  }

  return (
    currentWallet.currencyType === nextWallet.currencyType &&
    currentWallet.serverCurrencyType === nextWallet.serverCurrencyType &&
    currentWallet.allBalance === nextWallet.allBalance &&
    currentWallet.lockMoney === nextWallet.lockMoney
  );
};

const getPreferredWallet = (
  wallets: IFInitWalletRsp[],
  currentWallet?: IFInitWalletRsp
) => {
  if (!wallets.length) {
    return undefined;
  }

  return (
    wallets.find((wallet) => wallet.currencyType === currentWallet?.currencyType) ||
    wallets.find((wallet) => wallet.defaultCurrency) ||
    wallets[0]
  );
};

const DropdownWallet = () => {
  const { t } = useTranslationPlus("Wallet");
  const { openWallet, openIWDunlock } = useDialogManager();

  const [gameBetData] = useUpdateGlobalOriginalBalance();
  const { data, mutate, isLoading } = useWalletAll(false);
  const walletAllOriginal = data?.all || [];
  const [globalWallet, setGlobalWallet] = useGlobalWalletStore();
  const [currentWalletGlobal, setCurrentWalletGlobal] =
    useGlobalCurrentWallet();
  const { trigger } = useWalletSwitch();

  const allWallets = useMemo(
    () => globalWallet?.all || walletAllOriginal,
    [globalWallet?.all, walletAllOriginal]
  );
  const [currentWallet, setCurrentWallet] = useState<IFInitWalletRsp>();
  const [isOpen, setIsOpen] = useState(false);
  const [gameWin, setGameWin] = useState(false);
  const [tempData, setTempData] = useState<GameOriginalBetBack>();

  const rootRef = useRef<HTMLDivElement>(null);
  const eventTargetRef = useRef(EventMgr.crateTarget());
  const winAmountRef = useRef<HTMLDivElement>(null);
  const setGlobalWalletRef = useRef(setGlobalWallet);
  const setCurrentWalletGlobalRef = useRef(setCurrentWalletGlobal);
  const currentWalletRef = useRef(currentWallet);
  const currentWalletGlobalRef = useRef(currentWalletGlobal);

  useEffect(() => {
    setGlobalWalletRef.current = setGlobalWallet;
    setCurrentWalletGlobalRef.current = setCurrentWalletGlobal;
    currentWalletRef.current = currentWallet;
    currentWalletGlobalRef.current = currentWalletGlobal;
  });

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openDropdown = useCallback(() => {
    setIsOpen(true);
    mutate();
  }, [mutate]);

  const toggleDropdown = useCallback(() => {
    if (isOpen) {
      closeDropdown();
      return;
    }
    openDropdown();
  }, [closeDropdown, isOpen, openDropdown]);

  const switchWallet = useCallback(
    async (wallet: IFInitWalletRsp) => {
      closeDropdown();

      if (DISABLED_SWITCH_CURRENCIES.includes(wallet.currencyType)) {
        return;
      }

      setCurrentWallet(wallet);
      setCurrentWalletGlobalRef.current(wallet);

      try {
        await trigger({
          currencyType: wallet.serverCurrencyType || wallet.currencyType,
        });
      } catch (err) {
        console.log(err);
      }
    },
    [closeDropdown, trigger]
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        closeDropdown();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeDropdown, isOpen]);

  useEffect(() => {
    if (data && globalWallet !== data) {
      setGlobalWalletRef.current(data);
    }
  }, [data, globalWallet]);

  useEffect(() => {
    const nextWallet = getPreferredWallet(
      walletAllOriginal,
      currentWalletRef.current
    );

    if (nextWallet) {
      if (!isSameWallet(currentWalletRef.current, nextWallet)) {
        setCurrentWallet(nextWallet);
      }
      if (!isSameWallet(currentWalletGlobalRef.current, nextWallet)) {
        setCurrentWalletGlobalRef.current(nextWallet);
      }
    }
  }, [currentWallet?.currencyType, walletAllOriginal]);

  useEffect(() => {
    const wallet = currentWalletRef.current;

    if (
      currentWalletGlobal &&
      currentWalletGlobal.currencyType === wallet?.currencyType &&
      !isSameWallet(wallet, currentWalletGlobal)
    ) {
      setCurrentWallet(currentWalletGlobal);
    }
  }, [currentWalletGlobal]);

  useEffect(() => {
    if (!gameBetData) {
      return;
    }

    const wallet = currentWalletRef.current;
    const playerAmount = Number(gameBetData.playerAmount);
    if (
      Number.isFinite(playerAmount) &&
      playerAmount > 0 &&
      wallet &&
      wallet?.allBalance !== playerAmount
    ) {
      setCurrentWalletGlobalRef.current({
        ...wallet,
        allBalance: playerAmount,
      });
    }

    if (gameBetData.winAmount > 0) {
      setGameWin(true);
      setTempData(gameBetData);

      setTimeout(() => {
        setGameWin(false);
      }, 300);
    }
  }, [gameBetData]);

  useEffect(() => {
    const handleGuideChange = (currencyType: string) => {
      const wallet = allWallets.find(
        (item) => item.currencyType === currencyType
      );

      if (wallet) {
        switchWallet(wallet);
      }
    };

    EventMgr.getInstance().addEventListener(
      GUIDE_CHANGE_BIND_CURRENCY,
      handleGuideChange,
      eventTargetRef.current
    );

    return () => {
      EventMgr.getInstance().remove(
        GUIDE_CHANGE_BIND_CURRENCY,
        handleGuideChange,
        eventTargetRef.current
      );
    };
  }, [allWallets, switchWallet]);

  const renderAmount = (wallet: IFInitWalletRsp, amount: number) => (
    <CurrencyComponent
      amount={amount}
      showIcon={false}
      showUnit={false}
      type="valid9"
      currencyType={wallet.currencyType}
      serverCurrencyType={wallet.serverCurrencyType}
    />
  );

  const renderWalletOption = (wallet: IFInitWalletRsp, index: number) => {
    const isSelected = wallet.currencyType === currentWallet?.currencyType;
    const isFun = wallet.currencyType === "FUN" || wallet.currencyType === "LOCK_FUN";
    const optionId = `wallet-drop-down-option-${wallet.currencyType}-${index}`;

    const handleActivate = () => {
      if (isFun) {
        closeDropdown();
        openIWDunlock();
        return;
      }
      switchWallet(wallet);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleActivate();
      }
    };

    const handleUnlock = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      closeDropdown();
      openIWDunlock();
    };

    return (
      <div
        id={optionId}
        key={optionId}
        role="option"
        tabIndex={0}
        aria-selected={isSelected}
        className={classNames(styles.option, {
          [styles.optionSelected]: isSelected,
        })}
        onClick={handleActivate}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.optionCurrency}>
          <CurrencyComponent
            currencyType={wallet.currencyType}
            serverCurrencyType={wallet.serverCurrencyType}
          />
        </div>
        <div className={styles.optionBalance}>
          {renderAmount(wallet, wallet.allBalance)}
          {!!wallet.lockMoney && (
            <div className={styles.lock} onClick={handleUnlock}>
              <LockIcon />
              {renderAmount(wallet, wallet.lockMoney)}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <LoadingUI isLoading={isLoading} dataList={[1]}>
        <div className={styles.bar}>
          <button
            type="button"
            className={styles.trigger}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls="wallet-drop-down-listbox"
            onClick={toggleDropdown}
          >
            {!!currentWallet ? (
              <>
                <div className={styles.currencyLine}>
                  <CurrencyComponent
                    currencyType={currentWallet.currencyType}
                    serverCurrencyType={currentWallet.serverCurrencyType}
                  />
                  <ChevronDownIcon className={styles.arrow} />
                </div>
                <div className={styles.amountLine}>
                  {renderAmount(currentWallet, currentWallet.allBalance)}
                </div>
              </>
            ) : (
              <span className={styles.loading}>
                <SpinUI dot loading className={styles.loadingSpin} />
              </span>
            )}
          </button>

          <ButtonUI
            className={styles.deposit}
            icon={<Qb />}
            type="primary"
            onClick={() => {
              openWallet();
            }}
          >
            {t("Deposit")}
          </ButtonUI>
        </div>

        {isOpen && (
          <div
            id="wallet-drop-down-listbox"
            className={styles.panel}
            role="listbox"
          >
            {allWallets.map(renderWalletOption)}
          </div>
        )}

        <CSSTransition
          nodeRef={winAmountRef}
          in={gameWin}
          timeout={500}
          mountOnEnter={true}
          classNames="win-amount-csst"
          unmountOnExit={true}
        >
          <div ref={winAmountRef} className={styles.gameWinAmount}>
            +
            <CurrencyComponent
              type="valid9"
              showUnit={false}
              showIcon={false}
              currencyType={currentWallet?.currencyType}
              serverCurrencyType={currentWallet?.serverCurrencyType}
              amount={tempData?.winAmount}
            />
          </div>
        </CSSTransition>
      </LoadingUI>
    </div>
  );
};

export default DropdownWallet;
